import { useState, useRef, useEffect } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface SearchSelectOption {
  value: string;
  label: string;
  sublabel?: string;
  imageUrl?: string;
}

interface SearchSelectProps {
  value: string | null | undefined;
  onValueChange: (value: string | null) => void;
  options: SearchSelectOption[];
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
}

export function SearchSelect({
  value,
  onValueChange,
  options,
  placeholder = "Sélectionner...",
  emptyText = "Aucun résultat",
  disabled = false,
  className,
  "data-testid": testId,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (open) {
      setSearch("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full justify-between font-normal h-10",
            !selected && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate flex-1 text-left">
            {selected ? (
              <span className="flex items-center gap-2">
                {selected.imageUrl && (
                  <img
                    src={selected.imageUrl}
                    alt=""
                    className="h-5 w-5 rounded object-cover shrink-0"
                  />
                )}
                <span className="truncate">{selected.label}</span>
                {selected.sublabel && (
                  <span className="text-muted-foreground text-xs truncate">
                    — {selected.sublabel}
                  </span>
                )}
              </span>
            ) : (
              placeholder
            )}
          </span>
          <span className="flex items-center gap-1 shrink-0 ml-1">
            {selected && !disabled && (
              <X
                className="h-3.5 w-3.5 opacity-50 hover:opacity-100 cursor-pointer"
                data-testid={testId ? `${testId}-clear` : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  onValueChange(null);
                }}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        style={{ width: "max(var(--radix-popover-trigger-width), 18rem)" }}
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            ref={inputRef}
            placeholder={`Rechercher...`}
            value={search}
            onValueChange={setSearch}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options
                .filter((o) => {
                  if (!search) return true;
                  const s = search.toLowerCase();
                  return (
                    o.label.toLowerCase().includes(s) ||
                    (o.sublabel && o.sublabel.toLowerCase().includes(s))
                  );
                })
                .map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => {
                      onValueChange(option.value);
                      setOpen(false);
                    }}
                    data-testid={
                      testId ? `${testId}-option-${option.value}` : undefined
                    }
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option.imageUrl && (
                      <img
                        src={option.imageUrl}
                        alt=""
                        className="h-6 w-6 rounded object-cover shrink-0 mr-2"
                      />
                    )}
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-semibold leading-tight">{option.label}</span>
                      {option.sublabel && (
                        <span className="text-xs text-muted-foreground leading-tight mt-0.5">
                          {option.sublabel}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
